// Staff adventures view - adventure management

/**
 * @typedef {object} StaffAdventure
 * @property {string} slug
 * @property {string} name
 * @property {string} [description]
 * @property {string} [capsule_image_url]
 * @property {boolean} [published]
 * @property {string} [level]
 * @property {string} [players]
 * @property {string} [duration]
 */

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

// Pencil icon SVG
const pencilIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path
    d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"
  />
</svg>`;

// Trash icon SVG
const trashIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M3 6h18" />
  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
</svg>`;

/**
 * Staff adventures view component for managing adventures
 */
class StaffAdventuresView extends BaseComponent {
  static properties = {
    _adventures: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _showDeleteModal: { type: Boolean, state: true },
    _adventureToDelete: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .adventure-image {
      height: 200px;
      background-size: cover;
      background-position: center;
      position: relative;
    }

    .adventure-image.no-image {
      background-color: #f8f9fa;
    }
  `;

  constructor() {
    super();
    /** @type {Array<StaffAdventure>} */
    this._adventures = [];
    this._loading = true;
    this._error = null;
    this._showDeleteModal = false;
    this._adventureToDelete = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadAdventures();
  }

  async _loadAdventures() {
    this._loading = true;
    this._error = null;

    try {
      this._adventures = await api.get('/api/adventures');
    } catch (err) {
      console.error('Failed to load adventures:', err);
      this._error = err instanceof Error ? err.message : 'Failed to load adventures';
    } finally {
      this._loading = false;
    }
  }

  _goToNewAdventure() {
    router.navigate('/staff/adventures/new');
  }

  /** @param {string} slug */
  _editAdventure(slug) {
    router.navigate(`/staff/adventures/${slug}/edit`);
  }

  /** @param {string} slug */
  _deleteAdventure(slug) {
    this._adventureToDelete = slug;
    this._showDeleteModal = true;
  }

  async _confirmDelete() {
    if (!this._adventureToDelete) return;

    try {
      await api.delete(`/api/adventures/${this._adventureToDelete}`);
      this._adventureToDelete = null;
      this._showDeleteModal = false;
      await this._loadAdventures();
    } catch (err) {
      console.error('Failed to delete adventure:', err);
      this._error = err instanceof Error ? err.message : 'Failed to delete adventure';
    }
  }

  _cancelDelete() {
    this._showDeleteModal = false;
    this._adventureToDelete = null;
  }

  render() {
    return html`
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">Staff</li>
          <li class="breadcrumb-item active" aria-current="page">Adventures</li>
        </ol>
      </nav>

      ${this._error ? html` <div class="alert alert-danger">${this._error}</div> ` : ''}

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Adventures</h2>
        <button class="btn btn-primary" @click="${this._goToNewAdventure}">Create Adventure</button>
      </div>

      ${
        this._loading
          ? html`
            <div class="text-center py-4">
              <div class="spinner-border">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          `
          : this._adventures.length === 0
            ? html` <p class="text-muted">No adventures yet.</p> `
            : html`
              <div class="d-flex flex-wrap gap-3">
                ${this._adventures.map(
                  (adventure) => html`
                    <div style="flex: 1 1 300px; min-width: 300px; max-width: 350px;">
                      <div class="card h-100">
                        <div
                          class="adventure-image ${adventure.capsule_image_url ? '' : 'no-image'}"
                          style="${
                            adventure.capsule_image_url
                              ? `background-image: url('${api.getImageUrl(adventure.capsule_image_url)}');`
                              : ''
                          }"
                        >
                          ${
                            !adventure.capsule_image_url
                              ? html`
                                <div class="d-flex align-items-center justify-content-center h-100">
                                  <span class="text-muted">No Image</span>
                                </div>
                              `
                              : ''
                          }

                          <!-- Badges -->
                          <div class="position-absolute bottom-0 start-0 p-1">
                            ${
                              adventure.published
                                ? html` <span class="badge bg-success">Published</span> `
                                : html` <span class="badge bg-warning text-dark">Unpublished</span> `
                            }
                          </div>

                          <!-- Action buttons -->
                          <div class="position-absolute top-0 end-0 p-2 d-flex gap-1">
                            <button
                              class="btn btn-sm btn-outline-primary bg-white"
                              title="Edit"
                              @click="${() => this._editAdventure(adventure.slug)}"
                            >
                              ${pencilIcon}
                            </button>
                            <button
                              class="btn btn-sm btn-outline-danger bg-white"
                              title="Delete"
                              @click="${() => this._deleteAdventure(adventure.slug)}"
                            >
                              ${trashIcon}
                            </button>
                          </div>
                        </div>
                        <div class="card-body">
                          <h6 class="card-title">${adventure.name}</h6>
                          ${
                            adventure.description
                              ? html`
                                <div
                                  class="small text-muted mb-2"
                                  style="display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;"
                                >
                                  ${adventure.description}
                                </div>
                              `
                              : ''
                          }
                          ${
                            adventure.level || adventure.players || adventure.duration
                              ? html`
                                <div class="mt-2 small text-muted">
                                  ${adventure.level ? html`<span class="me-2">Level: ${adventure.level}</span>` : ''}
                                  ${
                                    adventure.players
                                      ? html`<span class="me-2">Players: ${adventure.players}</span>`
                                      : ''
                                  }
                                  ${adventure.duration ? html`<span>Duration: ${adventure.duration}</span>` : ''}
                                </div>
                              `
                              : ''
                          }
                        </div>
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
      }

      <!-- Delete Confirmation Modal -->
      ${
        this._showDeleteModal
          ? html`
            <div class="modal show d-block" style="background: rgba(0,0,0,0.5);">
              <div class="modal-dialog">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Delete Adventure</h5>
                    <button type="button" class="btn-close" @click="${this._cancelDelete}"></button>
                  </div>
                  <div class="modal-body">
                    <p>Are you sure you want to delete this adventure? This cannot be undone.</p>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="${this._cancelDelete}">Cancel</button>
                    <button type="button" class="btn btn-danger" @click="${this._confirmDelete}">Delete</button>
                  </div>
                </div>
              </div>
            </div>
          `
          : ''
      }
    `;
  }
}

customElements.define('staff-adventures-view', StaffAdventuresView);
